/**
 * `<StatusChip>` — the single status/condition chip used everywhere a combatant's statuses
 * render (a player's read-only unit-sheet statuses row, and the GM's editors — the EDIT
 * inspector's {@link StatusEditor} and the PLAY unit sheet's row — where each chip also carries a remove `×`). The chip is a
 * glossary {@link Term} (`combat-glossary` 3.3): hover, focus or tap it for the status's rule, read
 * through `glossaryEntry`'s `status:` key off the {@link statusBlurb} catalog.
 *
 * The popover adds what is true of this instance: the humanized `expiry` as a badge (omitted for
 * the default `until_removed`), and a GM ruling's reason and effect above the rule. A status the
 * catalog does not know (a homebrew free-text one, a GM ruling) still opens, with "No description
 * on file." unless a ruling explains it.
 *
 * A RAW `Hidden` chip also carries `status-holo`, the board's holo-ghost look in CSS (`decisions/0188`),
 * so the rail and inspector mark it the way the token does.
 */
import { HIDDEN_STATUS, type StatusInstance } from "@lancer-vtt/domain";
import { Term } from "../glossary/Term";
import { expiryLabel } from "./statusCatalog";
import { tipAttrs } from "../tooltip";
import { Icon } from "../Icon";
import { faMinus, faXmark } from "@fortawesome/free-solid-svg-icons";

/**
 * A one-line, plain reading of what a ruling actually DOES, so the tooltip states the mechanics
 * rather than leaving the player to infer them from the GM's prose. Deliberately describes only
 * what `compileRuling` emits — if this and the compiler ever disagree, this line is the lie.
 */
function rulingEffectLabel(ruling: NonNullable<StatusInstance["ruling"]>): string {
  const parts: string[] = [];
  const stacks = ruling.stacks ?? 1;
  if (ruling.accuracy !== undefined && ruling.accuracy !== 0) {
    const n = Math.abs(ruling.accuracy) * stacks;
    parts.push(ruling.accuracy > 0 ? `+${n} Accuracy` : `+${n} Difficulty`);
  }
  if (ruling.verb !== undefined) parts.push(`on ${ruling.verb} only`);
  if (parts.length === 0) parts.push("Advisory — no mechanical effect");
  // A reaction attack routes through the same resolver, so it consumes the ruling too. Say so
  // here rather than let it surprise someone mid-fight.
  if (ruling.consumeOnAttack === true) parts.push("consumed by your next attack, hit or miss (including a reaction)");
  return parts.join(" · ");
}

export function StatusChip({
  status,
  className,
  onRemove,
  onDecrement,
}: {
  status: StatusInstance;
  /** The chip's own visual class — the Term's wrapper carries it, so the wrapper IS the chip. */
  className: string;
  /** When set, the chip renders a remove `×` (the GM's status editors, EDIT and PLAY); omitted for read-only rails. */
  onRemove?: () => void;
  /** When set, a stacked ruling's chip also renders a `−` that takes one level off the stack. */
  onDecrement?: () => void;
}) {
  const expiry = expiryLabel(status.expiry);
  // A GM-minted ad-hoc ruling has no catalog entry — its whole description IS the GM's stated
  // reason: a player who can see WHY they have a bane will accept it, where an unexplained chip
  // reads as a bug. Rendered ahead of the catalog blurb so the reason is the first thing read, and
  // it replaces the "No description on file." fallback entirely (a ruling always has a reason —
  // the field is required).
  const ruling = status.ruling;
  // A stacked ruling reads "Rattled 2": one chip, the count after the name.
  const stacks = ruling?.stacks ?? 1;
  return (
    <Term
      k={`status:${status.name}`}
      className={status.name === HIDDEN_STATUS ? `${className} status-holo` : className}
      badges={[...(ruling ? ["GM ruling"] : []), ...(expiry ? [expiry] : [])]}
      note={
        ruling && (
          <>
            <p className="term-pop-note">{ruling.reason}</p>
            <p className="term-pop-note">{rulingEffectLabel(ruling)}</p>
          </>
        )
      }
      fallback={{ title: stacks > 1 ? `${status.name} ${stacks}` : status.name, kind: "Status", text: ruling ? "" : "No description on file." }}
    >
      {status.name}
      {stacks > 1 && <span className="status-chip-stacks">{` ${stacks}`}</span>}
      {onDecrement && stacks > 1 && (
        <button onClick={onDecrement} aria-label={`lower ${status.name}`} {...tipAttrs({ label: `Lower ${status.name} to ${stacks - 1}` })} className="cw-status-x cw-status-dec" type="button">
          <Icon icon={faMinus} />
        </button>
      )}
      {onRemove && (
        // An icon, not a "×" character, so the chip's text stays the status name.
        <button onClick={onRemove} aria-label={`remove ${status.name}`} {...tipAttrs({ label: `Remove ${status.name}` })} className="cw-status-x" type="button">
          <Icon icon={faXmark} />
        </button>
      )}
    </Term>
  );
}
